// Client-side CSV export for the analytics prospect table (CWA-159).
//
// The backend has its own export at GET /list?format=csv, and it cannot be used:
// the CSV branch re-queries with size = Int.MAX_VALUE, which trips that method's
// own `require(size in 1..100)` and returns 400 on every call. There is no
// parameter combination that makes it work, so the export is built from the rows
// already on the client instead (FR-039, research R7).
//
// That is a real limitation, not a hidden one: this covers the rows currently
// loaded for the filters in use, and the button and the filename both say so.
// When the backend is fixed, point the click handler at the endpoint and delete
// this file.

import { ProspectAnalyticsRow } from "../../../../types/onboardingAnalytics";
import { OnboardingState, LeadSource, SignMethod } from "../../../../types/onboarding";
import { stateLabel } from "../../../../utils/onboardingStates";
import {
  LEAD_SOURCE_LABELS,
  leadSourceLabel,
  signMethodLabel,
} from "../../../../constants/contractSigning";
import { humaniseSegment } from "../../../../utils/analyticsFormat";
import { setupFeeStatusLabel } from "../../../../utils/setupFee";

const HEADERS = [
  "Name",
  "Customer type",
  "Stage",
  "Lead source",
  "Vendor",
  "Sign method",
  "Created",
  "Last changed",
  "Days in stage",
  "Days total",
  "Last actor",
  "Setup fee",
];

/**
 * Quote a field for CSV. A value containing a comma, a quote or a newline is
 * quoted and its quotes doubled — a prospect's name or a free-text lead source
 * can contain any of them.
 */
const escapeCsv = (value: string): string =>
  /[",\n\r]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;

const cell = (value: string | number | null | undefined): string => {
  if (value == null) return "";
  return escapeCsv(String(value));
};

export const buildProspectCsv = (rows: ProspectAnalyticsRow[]): string => {
  const lines = [HEADERS.join(",")];

  rows.forEach((row) => {
    lines.push(
      [
        cell(row.fullName),
        cell(humaniseSegment(String(row.customerType))),
        // Labelled, not raw — an export is read by people too.
        cell(stateLabel(row.state as OnboardingState)),
        cell(
          row.leadSource
            ? String(row.leadSource) in LEAD_SOURCE_LABELS
              ? leadSourceLabel(row.leadSource as LeadSource)
              : humaniseSegment(String(row.leadSource))
            : ""
        ),
        cell(row.vendor),
        cell(row.signMethod ? signMethodLabel(row.signMethod as SignMethod) : ""),
        cell(row.createdAt),
        cell(row.lastChangedAt),
        cell(row.daysInState),
        cell(row.daysTotal),
        cell(row.lastActor),
        cell(row.setupFeeStatus ? setupFeeStatusLabel(row.setupFeeStatus) : ""),
      ].join(",")
    );
  });

  return lines.join("\n");
};

/** Filename says what the file actually contains, so nobody reads it as the full set. */
export const exportFilename = (): string =>
  `prospects-current-view-${new Date().toISOString().slice(0, 10)}.csv`;

export const downloadProspectCsv = (rows: ProspectAnalyticsRow[]): void => {
  const blob = new Blob([`﻿${buildProspectCsv(rows)}`], {
    type: "text/csv;charset=utf-8;",
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = exportFilename();
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};
